import { useMemo, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { useUserDetails } from 'api/useUserDetails';
import { Currency, OrderDetails } from 'common/contracts';

import DataTable from 'components/DataTableComponent/DataTable';

import {
  FREE_ORDER_TABLE_COLUMNS,
  ORDER_TABLE_COLUMNS,
  ORDER_TABLE_ROWS_PER_PAGE
} from '../constant';
import { createOrderTableRow } from '../utils';

interface OrderListProps {
  orders: OrderDetails[];
  currencyCodeToCurrencyMap: Map<string, Currency>;
  isLoading: boolean;
  isFree?: boolean;
  stickyHeader?: boolean;
  sortingState?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  infiniteScrollConfig?: {
    enabled: boolean;
    onLoadMore: () => void;
    hasMore: boolean;
    isLoadingMore?: boolean;
  };
}

export const OrderList = ({
  orders,
  currencyCodeToCurrencyMap,
  isLoading,
  isFree,
  stickyHeader,
  sortingState,
  onSortingChange,
  infiniteScrollConfig
}: OrderListProps) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { userDetails } = useUserDetails();

  const tableData = useMemo(() => {
    return orders.map((order) =>
      createOrderTableRow(order, currencyCodeToCurrencyMap, userDetails)
    );
  }, [orders, currencyCodeToCurrencyMap, userDetails]);

  const columns = useMemo(() => {
    return isFree ? FREE_ORDER_TABLE_COLUMNS : ORDER_TABLE_COLUMNS;
  }, [isFree]);

  const rowsPerPage = useMemo(() => {
    const rows = infiniteScrollConfig?.enabled
      ? orders.length || ORDER_TABLE_ROWS_PER_PAGE
      : ORDER_TABLE_ROWS_PER_PAGE;
    return rows;
  }, [infiniteScrollConfig?.enabled, orders.length]);

  return (
    <div className="overflow-hidden flex flex-col max-h-[calc(100vh-200px)] min-h-[500px]">
      <div ref={scrollContainerRef} className="overflow-auto flex-1">
        <DataTable
          key={`orders-${orders.length}-${isFree}`}
          enableColumnResizing
          stickyHeader={stickyHeader}
          data={tableData}
          columns={columns}
          rowIdIdentifier="id"
          onRowClick={(row) => {
            navigate(`/orders/${row.id}?publisherId=${row.publisherId}`);
          }}
          isLoading={isLoading}
          showPagination={false}
          rowsPerPage={rowsPerPage}
          sortingState={sortingState}
          onSortingChange={onSortingChange}
          infiniteScrollConfig={
            infiniteScrollConfig
              ? {
                  ...infiniteScrollConfig,
                  scrollContainerRef
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};
